import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { Div, ModalBackground } from '@icecreamsocial/components';
import { useDismiss, usePostMessage } from '../../hooks';
import {
	FlavorAnimator,
	Footer,
	CloseButton,
	OrderIdDisplay,
} from '../../presentational';
import { Hero } from '..';
import { useAtom } from 'jotai';
import {
	campaignAtom,
	displayOrderIdAtom,
	flavorAtom,
	openAtom,
} from '../../../shared/atoms';

const WIPE_IN_DURATION = 500;
const MODAL_ANIMATION_DELAY = WIPE_IN_DURATION * 0.9;

/**
 * @description
 * Handles the layout of the widget depending on the provided flavor.  Automatically adapts
 * layout based on shared
 * @todo
 * consider moving this to a "container" component
 */
export const FlavorLayout = ({ children }) => {
	const [flavor] = useAtom(flavorAtom);
	const [open] = useAtom(openAtom);
	const [{ campaign }] = useAtom(campaignAtom);
	const [displayOrderId] = useAtom(displayOrderIdAtom);

	const handleDismiss = useDismiss();
	const layoutRef = useRef();
	const dispatchMessage = usePostMessage();

	useEffect(() => {
		const resizeObserver = new ResizeObserver((entries) => {
			const { contentRect } = entries[0];

			const data = {
				height: contentRect.height,
				width: contentRect.width,
			};

			dispatchMessage(
				{
					type: 'ics_resize',
					data,
				},
				'*'
			);
		});

		resizeObserver.observe(layoutRef.current);

		return () => resizeObserver.unobserve(layoutRef.current);
	});

	return (
		<Div
			ref={layoutRef}
			position='relative'
			display='flex'
			alignItems='center'
			justifyContent='center'
			elWidth='100%'
			elHeight={flavor === 'embedded' ? '100%' : '100vh'}
			zIndex='999999'
		>
			{flavor !== 'embedded' && (
				<CloseButton
					id='close'
					position='absolute'
					display={open ? ['none', 'block'] : 'none'}
					zIndex='modal'
					backgroundColor='rgba(34,34,34, 0.5)'
					textColor='bodyInverse'
					padding='sm'
					positionTop='16px'
					positionRight='16px'
					onClick={handleDismiss()}
					aria-label='close'
					data-slate-cta
				/>
			)}
			<FlavorAnimator
				open={open}
				flavor={flavor}
				duration={WIPE_IN_DURATION}
				delay={MODAL_ANIMATION_DELAY}
				elWidth={
					flavor === 'modal' ? ['100%', 'clamp(50%, 65%, 1440px)'] : '100%'
				}
				elHeight={flavor === 'modal' ? ['100vh', '80vh', '70vh'] : '100%'}
				display='flex'
				flexDirection={['column', 'column', 'row']}
				position='relative'
				borderRadius={flavor === 'modal' ? ['0', 'default'] : '0'}
				overflow={['auto', 'hidden']}
			>
				{displayOrderId ? (
					<OrderIdDisplay
						orderId={displayOrderId}
						onOrderClicked={handleDismiss()}
						position='absolute'
						positionTop='16px'
						positionLeft='16px'
						zIndex='1'
					/>
				) : (
					flavor !== 'embedded' && (
						<CloseButton
							id='close_mobile'
							position='absolute'
							display={['block', 'none']}
							zIndex='1'
							backgroundColor='rgba(34,34,34, 0.5)'
							textColor='bodyInverse'
							padding='sm'
							positionTop='16px'
							positionRight='16px'
							onClick={handleDismiss()}
							data-slate-cta
						/>
					)
				)}
				<Div
					display='flex'
					alignItems='center'
					backgroundColor='body'
					elWidth={['100%', '100%', '56%']}
					elMinWidth='50%'
					elMinHeight='240px'
					style={{
						clipPath: 'inset(0)',
					}}
				>
					<Hero />
				</Div>
				<Div
					display='flex'
					flexGrow='1'
					flexDirection='column'
					elWidth={['100%', '100%', '44%']}
					borderTopLeftRadius={['default', 'default', '0']}
					borderTopRightRadius={['default', 'default', '0']}
					marginTop={['-16px', '-16px', '0']}
					backgroundColor='bodyInverse'
					overflowY={['visible', 'auto']}
					zIndex='1'
				>
					{children}
					<Footer
						termsURL={campaign?.termsAndConditionsURL}
						privacyURL={campaign?.privacyPolicyURL}
					/>
				</Div>
			</FlavorAnimator>
		</Div>
	);
};
